import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import Task from "../../../interfaces/task/Task";
import NoDataFound from "./NoDataFound";
import "./taskpage.css";

interface InstructionsPopupProps {
  task: Task;
  anchorElement: HTMLElement | SVGSVGElement;
  onClose: () => void;
}

const InstructionsPopup: React.FC<InstructionsPopupProps> = ({
  task,
  anchorElement,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorElement) return;

    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      const popupWidth = 400; // Approximate width of the popup
      const popupHeight = 500; // Approximate height
      const padding = 10;

      // Calculate position - show to the right of the icon, or left if not enough space
      let leftPos = rect.right + window.scrollX + padding;
      const rightSpace = window.innerWidth - rect.right;

      // If not enough space on right, show on left
      if (rightSpace < popupWidth + padding) {
        leftPos = rect.left + window.scrollX - popupWidth - padding;
      }

      // Ensure popup doesn't go off screen
      if (leftPos < padding) {
        leftPos = padding;
      }
      if (leftPos + popupWidth > window.innerWidth - padding) {
        leftPos = window.innerWidth - popupWidth - padding;
      }

      // Vertical position - align with icon or adjust if needed
      let topPos = rect.top + window.scrollY;
      const bottomSpace = window.innerHeight - rect.bottom;

      // If not enough space below, show above
      if (bottomSpace < popupHeight && rect.top > popupHeight) {
        topPos = rect.top + window.scrollY - popupHeight;
      }

      // Ensure popup doesn't go off screen vertically
      if (topPos < window.scrollY + padding) {
        topPos = window.scrollY + padding;
      }
      if (topPos + popupHeight > window.scrollY + window.innerHeight - padding) {
        topPos = window.scrollY + window.innerHeight - popupHeight - padding;
      }

      setPosition({ top: topPos, left: leftPos });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorElement]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, anchorElement]);

  return (
    <>
      {/* Backdrop with overlay effect */}
      <div
        className="instructions-popup-backdrop"
        onClick={onClose}
      />

      {/* Popup */}
      <div
        ref={popupRef}
        className="instructions-popup task-details-card"
        style={{
          position: "absolute",
          top: position.top,
          left: position.left,
          width: "400px",
          maxWidth: "90vw",
          maxHeight: "500px",
          zIndex: 9999,
          backgroundColor: "#fff",
          borderRadius: "0.71363rem",
          border: "1.142px solid #e5e7eb",
          boxShadow: "0 4px 15px 0 rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Tab Header - Only Instructions */}
        <div className="details-tabs">
          <span className="tab active">Task Instructions</span>
        </div>

        {/* Content */}
        <div className="details-content" style={{ flex: 1, overflowY: "auto" }}>
          <div className="task-instructions-content">
            {task.information ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(task.information),
                }}
              />
            ) : (
              <NoDataFound
                title="No Task Instructions Found"
                description="No instructions have been defined for this task type"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InstructionsPopup;

